import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type {
  WizardEngine,
  WizardFlowConfig,
  WizardStepConfig,
  TournamentDraft,
  TournamentDraftSlices,
  NavigationResult,
} from './types';
import { useWizardStore } from '../store';

// ============================================================================
// CONTEXT
// ============================================================================

const WizardContext = createContext<WizardEngine | null>(null);

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface WizardProviderProps {
  children: ReactNode;
  flowConfig: WizardFlowConfig;
  onSubmit: (draft: TournamentDraft) => Promise<void>;
  onFlowChange?: (newFlowId: string) => void;
}

// ============================================================================
// PROVIDER IMPLEMENTATION
// ============================================================================

export function WizardProvider({
  children,
  flowConfig,
  onSubmit,
  onFlowChange,
}: WizardProviderProps) {
  // Store access
  const draft = useWizardStore((state) => state.draft);
  const updateSlice = useWizardStore((state) => state.updateSlice);
  const setSlice = useWizardStore((state) => state.setSlice);
  const setCurrentStep = useWizardStore((state) => state.setCurrentStep);
  const markStepComplete = useWizardStore((state) => state.markStepComplete);
  const isStepCompleted = useWizardStore((state) => state.isStepCompleted);
  const clearDraft = useWizardStore((state) => state.clearDraft);

  const isSubmitting = useWizardStore(() => false);

  // ========================================
  // Computed: Visible steps (filtered by isVisible)
  // ========================================
  const visibleSteps = useMemo((): WizardStepConfig[] => {
    if (!draft) return [];

    return flowConfig.steps.filter((step) => {
      if (!step.isVisible) return true;
      return step.isVisible(draft);
    });
  }, [flowConfig.steps, draft]);

  // ========================================
  // Computed: Current step info
  // ========================================
  const currentStepIndex = useMemo((): number => {
    if (!draft) return 0;
    const index = visibleSteps.findIndex((s) => s.id === draft.currentStepId);
    return index >= 0 ? index : 0;
  }, [visibleSteps, draft]);

  const currentStep = useMemo((): WizardStepConfig => {
    return visibleSteps[currentStepIndex] ?? visibleSteps[0];
  }, [visibleSteps, currentStepIndex]);

  // ========================================
  // Computed: Navigation state
  // ========================================
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;
  const isSummaryStep = currentStep?.id === 'summary';

  // ========================================
  // Validation
  // ========================================
  const validateStep = useCallback(
    (stepConfig: WizardStepConfig): boolean => {
      if (!draft) return false;

      // If no validation schema, consider it valid
      const key = stepConfig.draftSliceKey;
      if (key == null) return false;

      if (!stepConfig.validationSchema) {
        const sliceData = draft[key];
        return sliceData !== null;
      }

      const sliceData = draft[key];
      if (!sliceData) return false;

      const result = stepConfig.validationSchema.safeParse(sliceData);
      return result.success;
    },
    [draft]
  );

  const validateCurrentStep = useCallback((): boolean => {
    if (!currentStep) return false;
    return validateStep(currentStep);
  }, [currentStep, validateStep]);

  // ========================================
  // Step completion checks
  // ========================================
  const isStepComplete = useCallback(
    (stepId: string): boolean => {
      // First check if it's marked as completed in the draft
      if (!isStepCompleted(stepId)) return false;

      // Then validate the data is still valid
      const stepConfig = visibleSteps.find((s) => s.id === stepId);
      if (!stepConfig) return false;

      return validateStep(stepConfig);
    },
    [isStepCompleted, visibleSteps, validateStep]
  );

  const isStepAccessible = useCallback(
    (stepId: string): boolean => {
      const targetIndex = visibleSteps.findIndex((s) => s.id === stepId);
      if (targetIndex < 0) return false;

      // Can always go back to completed steps
      if (targetIndex < currentStepIndex) {
        return isStepComplete(stepId) || targetIndex === 0;
      }

      // Can only go to current step
      if (targetIndex === currentStepIndex) return true;

      // Cannot jump forward to incomplete steps
      // All previous steps must be complete
      for (let i = 0; i < targetIndex; i++) {
        const prevStep = visibleSteps[i];
        if (!isStepComplete(prevStep.id)) return false;
      }

      return true;
    },
    [visibleSteps, currentStepIndex, isStepComplete]
  );

  // ========================================
  // Navigation: Can navigate?
  // ========================================
  const canGoNext = useMemo((): boolean => {
    if (isLastStep) return false;
    return validateCurrentStep();
  }, [isLastStep, validateCurrentStep]);

  const canGoBack = useMemo((): boolean => {
    return !isFirstStep;
  }, [isFirstStep]);

  // ========================================
  // Navigation: Actions
  // ========================================
  const goNext = useCallback((): NavigationResult => {
    if (!draft) {
      return { success: false, error: 'No hay draft activo' };
    }

    if (isLastStep) {
      return { success: false, error: 'Ya estás en el último paso' };
    }

    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      return {
        success: false,
        error: 'Completa todos los campos requeridos antes de continuar',
      };
    }

    // Mark current step as complete
    markStepComplete(currentStep.id);

    // Move to next step
    const nextStep = visibleSteps[currentStepIndex + 1];
    if (!nextStep) {
      return { success: false, error: 'No se encontró el siguiente paso' };
    }

    setCurrentStep(nextStep.id);

    return { success: true, targetStepId: nextStep.id };
  }, [
    draft,
    isLastStep,
    validateCurrentStep,
    markStepComplete,
    currentStep,
    visibleSteps,
    currentStepIndex,
    setCurrentStep,
  ]);

  const goBack = useCallback((): NavigationResult => {
    if (!draft) {
      return { success: false, error: 'No hay draft activo' };
    }

    if (isFirstStep) {
      return { success: false, error: 'Ya estás en el primer paso' };
    }

    // Always allow going back (no validation required)
    const prevStep = visibleSteps[currentStepIndex - 1];
    if (!prevStep) {
      return { success: false, error: 'No se encontró el paso anterior' };
    }

    setCurrentStep(prevStep.id);

    return { success: true, targetStepId: prevStep.id };
  }, [draft, isFirstStep, visibleSteps, currentStepIndex, setCurrentStep]);

  const goTo = useCallback(
    (stepId: string): NavigationResult => {
      if (!draft) {
        return { success: false, error: 'No hay draft activo' };
      }

      const targetIndex = visibleSteps.findIndex((s) => s.id === stepId);
      if (targetIndex < 0) {
        return { success: false, error: `Paso "${stepId}" no encontrado` };
      }

      // Going back - always allowed to completed steps
      if (targetIndex < currentStepIndex) {
        // Can go back to first step or any completed step
        if (targetIndex === 0 || isStepComplete(stepId)) {
          setCurrentStep(stepId);
          return { success: true, targetStepId: stepId };
        }
        return {
          success: false,
          error: 'Solo puedes volver a pasos completados',
        };
      }

      // Going forward - must complete current step first
      if (targetIndex > currentStepIndex) {
        // Check if all steps between current and target are complete
        for (let i = currentStepIndex; i < targetIndex; i++) {
          const step = visibleSteps[i];
          if (!isStepComplete(step.id)) {
            return {
              success: false,
              error: `Debes completar "${step.title}" primero`,
            };
          }
        }

        setCurrentStep(stepId);
        return { success: true, targetStepId: stepId };
      }

      // Same step - no-op
      return { success: true, targetStepId: stepId };
    },
    [draft, visibleSteps, currentStepIndex, isStepComplete, setCurrentStep]
  );

  // ========================================
  // Draft management
  // ========================================
  const updateStepData = useCallback(
    <K extends keyof TournamentDraftSlices>(
      key: K,
      data: Partial<NonNullable<TournamentDraftSlices[K]>>
    ): void => {
      updateSlice(key, data);
    },
    [updateSlice]
  );

  const setStepData = useCallback(
    <K extends keyof TournamentDraftSlices>(
      key: K,
      data: TournamentDraftSlices[K]
    ): void => {
      setSlice(key, data);
    },
    [setSlice]
  );

  // ========================================
  // Flow management
  // ========================================
  const changeFlow = useCallback(
    (newFlowId: string, _preserveSportSlice?: boolean): void => {
      // Just notify parent - let parent handle the actual flow change
      // This keeps the WizardContext focused on the current flow
      // and lets TournamentWizardPage orchestrate flow transitions
      onFlowChange?.(newFlowId);
    },
    [onFlowChange]
  );

  // ========================================
  // Lifecycle
  // ========================================
  const reset = useCallback((): void => {
    clearDraft();
  }, [clearDraft]);

  const submit = useCallback(async (): Promise<void> => {
    if (!draft) {
      throw new Error('No hay draft para enviar');
    }

    // Validate all steps before submitting
    for (const step of visibleSteps) {
      if (!validateStep(step)) {
        throw new Error(`El paso "${step.title}" no está completo`);
      }
    }

    await onSubmit(draft);
  }, [draft, visibleSteps, validateStep, onSubmit]);

  // ========================================
  // Engine object
  // ========================================
  const engine = useMemo(
    (): WizardEngine => ({
      // Current state
      draft: draft!,
      flowConfig,
      currentStep,
      currentStepIndex,
      visibleSteps,

      // Navigation state
      canGoNext,
      canGoBack,
      isFirstStep,
      isLastStep,
      isSummaryStep,

      // Navigation actions
      goNext,
      goBack,
      goTo,

      // Draft management
      updateStepData,
      setStepData,

      // Step validation
      isStepComplete,
      isStepAccessible,
      validateCurrentStep,

      // Flow management
      changeFlow,

      // Lifecycle
      reset,
      submit,
      isSubmitting,
    }),
    [
      draft,
      flowConfig,
      currentStep,
      currentStepIndex,
      visibleSteps,
      canGoNext,
      canGoBack,
      isFirstStep,
      isLastStep,
      isSummaryStep,
      goNext,
      goBack,
      goTo,
      updateStepData,
      setStepData,
      isStepComplete,
      isStepAccessible,
      validateCurrentStep,
      changeFlow,
      reset,
      submit,
      isSubmitting,
    ]
  );

  // Don't render if no draft
  if (!draft) {
    return null;
  }

  return (
    <WizardContext.Provider value={engine}>{children}</WizardContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access the wizard engine.
 * Must be used within a WizardProvider.
 */
export function useWizard(): WizardEngine {
  const context = useContext(WizardContext);

  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }

  return context;
}

/**
 * Hook to access the wizard engine, returning null if not in a provider.
 * Useful for components that may or may not be inside the wizard.
 */
export function useWizardOptional(): WizardEngine | null {
  return useContext(WizardContext);
}
